import { Auth, Hub } from "aws-amplify";
import awsExports from "../../aws-exports";
import { clearImageryEmailCookie } from "@/lib/cookies";

type AmplifyAuthConfig = {
	Auth: {
		region: string;
		identityPoolRegion?: string;
		userPoolId: string;
		userPoolWebClientId: string;
		mandatorySignIn?: boolean;
		authenticationFlowType?: "USER_PASSWORD_AUTH" | "CUSTOM_AUTH";
		oauth?: {
			domain: string;
			redirectSignIn: string;
			redirectSignOut: string;
			responseType: "code" | "token";
		};
	};
};

export const getOrigin = (): string => {
	if (typeof window === "undefined") return "";
	return window.location.origin;
};

const baseAmplifyConfig = (
	flow: "USER_PASSWORD_AUTH" | "CUSTOM_AUTH" = "USER_PASSWORD_AUTH"
): AmplifyAuthConfig => {
	const origin = getOrigin();
	// Use values from aws-exports.js, with env vars as fallback
	const region =
		awsExports.aws_project_region ||
		process.env.NEXT_PUBLIC_AWS_REGION ||
		"";
	const userPoolId =
		awsExports.aws_user_pools_id ||
		process.env.NEXT_PUBLIC_COGNITO_USER_POOL_ID ||
		"";
	const userPoolWebClientId =
		awsExports.aws_user_pools_web_client_id ||
		process.env.NEXT_PUBLIC_COGNITO_USER_POOL_CLIENT_ID ||
		"";
	const cognitoDomain = process.env.NEXT_PUBLIC_COGNITO_DOMAIN; // OAuth domain if configured

	return {
		Auth: {
			region,
			identityPoolRegion: awsExports.aws_cognito_region || region,
			userPoolId,
			userPoolWebClientId,
			mandatorySignIn:
				(process.env.NEXT_PUBLIC_COGNITO_MANDATORY_SIGNIN ||
					"false") === "true",
			authenticationFlowType: flow,
			oauth: cognitoDomain
				? {
						domain: cognitoDomain,
						redirectSignIn: `${origin}/login`,
						redirectSignOut: `${origin}/login`,
						responseType: "code",
					}
				: undefined,
		},
	};
};

const configureAmplifyUserPasswordAuth = () => {
	return Auth.configure(baseAmplifyConfig("USER_PASSWORD_AUTH"));
};

export const configureOTPAuthAmplifyClient = () => {
	return Auth.configure(baseAmplifyConfig("CUSTOM_AUTH"));
};

interface CognitoSignInResponse {
	challengeName?: string;
	[key: string]: unknown;
}

export const loginCognito = async (
	username: string,
	password: string
): Promise<CognitoSignInResponse> => {
	configureAmplifyUserPasswordAuth();
	const response = (await Auth.signIn({ username, password }).catch(
		(error: unknown) => {
			throw error;
		}
	)) as CognitoSignInResponse;
	if (response?.challengeName === "SMS_MFA") {
		Hub.dispatch("auth", { event: "signInMFAInitited" });
	} else if (response?.challengeName === "NEW_PASSWORD_REQUIRED") {
		// surface this to caller; handled via exception/response shape
	}
	return response;
};

export const getCurrentSession = async () => {
	configureAmplifyUserPasswordAuth();
	return Auth.currentSession();
};

export const getCurrentUser = async (opts?: { bypassCache: boolean }) => {
	configureAmplifyUserPasswordAuth();
	return Auth.currentAuthenticatedUser(opts);
};

/** Email for the current Cognito session (standard or federated attributes). */
export const getCognitoUserEmail = async (): Promise<string | null> => {
	try {
		configureAmplifyUserPasswordAuth();
		const user = await Auth.currentAuthenticatedUser({ bypassCache: true });
		if (!user) return null;

		let email = "";
		if (user.attributes) {
			email = user.attributes.email || "";
		}
		if (
			(!email || !email.includes("@")) &&
			user.UserAttributes &&
			Array.isArray(user.UserAttributes)
		) {
			const emailAttr = user.UserAttributes.find(
				(attr: { Name: string; Value: string }) => attr.Name === "email"
			);
			if (emailAttr?.Value) email = emailAttr.Value;
		}
		return email.includes("@") ? email : null;
	} catch {
		return null;
	}
};

/**
 * Exchange authorization code for tokens
 * This is needed for OAuth flows (Google, Microsoft) when using manual URL construction
 */
export const exchangeCodeForTokens = async (
	code: string,
	redirectUri: string
): Promise<{
	id_token: string;
	access_token: string;
	refresh_token: string;
	token_type: string;
	expires_in: number;
}> => {
	const domain = "https://auth.taxlienvest.com";
	const clientId =
		awsExports.aws_user_pools_web_client_id ||
		process.env.NEXT_PUBLIC_COGNITO_USER_POOL_CLIENT_ID ||
		"";

	const tokenEndpoint = `${domain}/oauth2/token`;

	const params = new URLSearchParams({
		grant_type: "authorization_code",
		client_id: clientId,
		code: code,
		redirect_uri: redirectUri,
	});

	try {
		console.log("Token exchange - Redirect URI:", redirectUri);
		console.log("Token exchange - Code length:", code.length);

		const response = await fetch(tokenEndpoint, {
			method: "POST",
			headers: {
				"Content-Type": "application/x-www-form-urlencoded",
			},
			body: params.toString(),
		});

		if (!response.ok) {
			const errorText = await response.text();
			let errorData;
			try {
				errorData = JSON.parse(errorText);
			} catch {
				errorData = {
					error: "invalid_grant",
					error_description: errorText || "Unknown error",
				};
			}

			console.error("Token exchange error response:", {
				status: response.status,
				statusText: response.statusText,
				error: errorData.error,
				error_description: errorData.error_description,
				redirect_uri_used: redirectUri,
			});

			throw new Error(
				errorData.error_description ||
					errorData.error ||
					`Token exchange failed: ${response.statusText}`
			);
		}

		const data = await response.json();
		console.log("Token exchange successful");
		return data;
	} catch (error) {
		console.error("Token exchange failed:", error);
		throw error;
	}
};

/**
 * Set Cognito session from tokens obtained via OAuth
 * Uses localStorage to store tokens in Amplify's expected format
 */
export const setCognitoSessionFromTokens = async (
	idToken: string,
	accessToken: string,
	refreshToken: string
): Promise<void> => {
	try {
		// Configure Amplify first
		configureAmplifyUserPasswordAuth();

		// Decode the ID token to get username
		const tokenPayload = JSON.parse(
			Buffer.from(idToken.split(".")[1], "base64").toString()
		);
		const username = tokenPayload.sub || tokenPayload["cognito:username"];

		const clientId =
			awsExports.aws_user_pools_web_client_id ||
			process.env.NEXT_PUBLIC_COGNITO_USER_POOL_CLIENT_ID ||
			"";

		const keyPrefix = `CognitoIdentityServiceProvider.${clientId}`;

		// Store tokens in Amplify's expected format in localStorage
		if (typeof window !== "undefined") {
			localStorage.setItem(`${keyPrefix}.${username}.idToken`, idToken);
			localStorage.setItem(
				`${keyPrefix}.${username}.accessToken`,
				accessToken
			);
			localStorage.setItem(
				`${keyPrefix}.${username}.refreshToken`,
				refreshToken
			);
			localStorage.setItem(`${keyPrefix}.LastAuthUser`, username);
		}

		// Dispatch auth event to notify Amplify and other listeners (like OnboardingContext) of the sign-in
		// This ensures the profile is refreshed after OAuth login
		Hub.dispatch("auth", {
			event: "signIn",
			data: {
				username,
			},
		});

		// Also dispatch cognitoHostedUI event for OAuth flows
		Hub.dispatch("auth", {
			event: "cognitoHostedUI",
			data: {
				username,
			},
		});
	} catch (error) {
		console.error("Failed to set Cognito session:", error);
		throw error;
	}
};

export const signOutUser = async () => {
	await Auth.signOut();
	clearImageryEmailCookie();
};

export const initiateForgotPassword = async (username: string) => {
	configureAmplifyUserPasswordAuth();
	return Auth.forgotPassword(username);
};

export const confirmNewPassword = async (
	username: string,
	code: string,
	password: string
) => {
	configureAmplifyUserPasswordAuth();
	return Auth.forgotPasswordSubmit(username, code, password);
};

export const resetTemporaryPassword = async (
	user: unknown,
	password: string
) => {
	configureAmplifyUserPasswordAuth();
	return Auth.completeNewPassword(user, password);
};

export const confirmUserLogin = async (user: unknown, code: string) => {
	configureAmplifyUserPasswordAuth();
	return Auth.confirmSignIn(user, code, "SMS_MFA");
};

export const confirmUser = async (username: string, code: string) => {
	configureAmplifyUserPasswordAuth();
	const data = await Auth.confirmSignUp(username, code);
	Hub.dispatch("auth", { event: "userConfirmation" });
	return data;
};

interface ResendSignupInput {
	username: string;
}

interface SignupInput {
	email?: string;
	password?: string;
	userContact?: string;
	loginViaPhone?: boolean;
	username?: string;
	name?: string;
}

interface UserSignupParams {
	username: string;
	password: string;
	attributes: {
		email?: string;
		phone_number?: string;
		name?: string;
	};
}

export const resendSignup = async (input: ResendSignupInput) => {
	configureAmplifyUserPasswordAuth();
	return Auth.resendSignUp(input.username);
};

export const signupCognito = async (input: SignupInput) => {
	configureAmplifyUserPasswordAuth();
	const userEmail = input.email;
	const userPassword = input.password ? input.password : input.userContact;
	const userPhone = input.loginViaPhone ? input.userContact : undefined;
	const userName = input.username || userEmail || "";
	const name = input.name;

	if (!userPassword) {
		throw new Error("Password is required for signup");
	}

	if (!userName) {
		throw new Error("Username or email is required for signup");
	}

	const userSignupParams: UserSignupParams = {
		username: userName,
		password: userPassword,
		attributes: {},
	};

	if (userEmail) userSignupParams.attributes.email = userEmail;
	if (userPhone) userSignupParams.attributes.phone_number = userPhone;
	if (name) userSignupParams.attributes.name = name;

	return Auth.signUp(userSignupParams);
};

// https://lien-vest.auth.us-east-1.amazoncognito.com/oauth2/authorize?
// response_type=code&
// client_id=6qfavumcq8cqgmuk2gbnn4kjrv&
// redirect_uri=https://www.taxlienvest.com&identity_provider=Microsoft&
// scope=openid+email+profile

export const signInWithProvider = async (provider: "Google" | "Microsoft") => {
	// Use Hosted UI via Amplify if configured; otherwise construct the URL manually
	const domain = "https://auth.taxlienvest.com";
	const clientId =
		awsExports.aws_user_pools_web_client_id ||
		process.env.NEXT_PUBLIC_COGNITO_USER_POOL_CLIENT_ID ||
		"";
	const origin = getOrigin();
	const redirectUri = `${origin}/login`;
	const scope = encodeURIComponent("openid email profile");

	// Always use manual URL construction to ensure redirect URI consistency
	// This ensures the redirect URI used in authorization matches the one used in token exchange
	// Also allows us to add prompt=select_account for Google

	// Manual fallback URL (matches pattern used in legacy TopNavMenu)
	const base = `${domain}/oauth2/authorize`;
	const url =
		`${base}?response_type=code` +
		`&client_id=${encodeURIComponent(clientId || "")}` +
		`&redirect_uri=${encodeURIComponent(redirectUri)}` +
		`&identity_provider=${encodeURIComponent(provider)}` +
		`&scope=${scope}` +
		// Force account selection screen for Google
		(provider === "Google" ? `&prompt=select_account` : "");
	if (typeof window !== "undefined") {
		window.location.href = url;
	}
};
